import { cn } from '@/lib/utils';
import type { Mold, WorkOrder } from '@/types/maintenance';

export type ComputedMoldStatus = 'available' | 'in_production' | 'in_maintenance' | 'on_hold' | 'blocked';

const statusConfig: Record<ComputedMoldStatus, { label: string; className: string }> = {
  available: {
    label: 'Available',
    className: 'bg-muted text-muted-foreground border-border',
  },
  in_production: {
    label: 'In Production',
    className: 'bg-molten/10 text-molten-deep border-molten/20',
  },
  in_maintenance: {
    label: 'In Maintenance',
    className: 'bg-muted text-foreground border-border',
  },
  on_hold: {
    label: 'On Hold',
    className: 'bg-status-amber-bg text-status-amber border-status-amber/40',
  },
  blocked: {
    label: 'Blocked',
    className: 'bg-status-amber-bg text-status-amber border-status-amber',
  },
};

interface ComputedStatusBadgeProps {
  status: ComputedMoldStatus;
  size?: 'sm' | 'md' | 'lg';
}

export function ComputedStatusBadge({ status, size = 'md' }: ComputedStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded border font-medium',
        size === 'sm' && 'px-1.5 py-0.5 text-xs',
        size === 'md' && 'px-2 py-1 text-sm',
        size === 'lg' && 'px-3 py-1.5 text-base',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

export function computeMoldStatus(
  mold: Mold,
  openWorkOrders: WorkOrder[]
): ComputedMoldStatus {
  // Blocked if open WO with blocking impact
  const hasBlockingWO = openWorkOrders.some(
    wo => ['line_down', 'safety', 'quality'].includes(wo.impact)
  );
  if (hasBlockingWO) {
    return 'blocked';
  }

  // On Hold if manual hold
  if (mold.status === 'on_hold') {
    return 'on_hold';
  }

  // In Maintenance if status says so
  if (mold.status === 'in_maintenance') {
    return 'in_maintenance';
  }

  // In Production if currently on a line
  if (mold.status === 'in_production' || mold.location === 'line') {
    return 'in_production';
  }

  return 'available';
}
